/**
 * SecondaryDetailPanel — renders a *stack* of side-by-side detail panels next
 * to the primary one. Each entry in the stack maps to one `SecondaryDetail`
 * `{ type, id }` reference (produced by clicking a "Linked Records" pill in
 * any detail panel) and renders as a slide-over offset by its position in
 * the stack so the user can keep multiple linked records open at once and
 * peel them off one at a time.
 *
 * The first stack entry (`stackIndex = 1`) sits immediately to the left of
 * the primary panel; subsequent entries (`2`, `3`, ...) cascade further
 * left. Z-index is descending so the leftmost panel renders below.
 */

import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { useFlagDismissals } from "../flags";
import { SecurityInterestDetailPanel } from "../securityInterests/SecurityInterestDetailPanel";
import { AssetPanel } from "../assetList/asset-list/AssetPanel";
import { LiabilityPanel } from "../generalLiabilities/LiabilityPanel";
import { LoanDetailPanel } from "../loansSummary/LoanDetailPanel";
import { AtoSroDetailSheet } from "../atoSro/AtoSroPanel";
import { directorDisclosureLabel } from "@/lib/director-disclosure";
import type { RecordType } from "./linkShared";

/* eslint-disable @typescript-eslint/no-explicit-any */

export interface SecondaryDetail {
  type: RecordType;
  id: string;
}

interface SecondaryDetailPanelProps {
  jobId: Id<"jobs">;
  /**
   * Ordered stack of open linked records. Index 0 is closest to the primary,
   * each subsequent entry stacks further left. Empty array renders nothing.
   *
   * `detail` is still accepted (single-entry shorthand) for back-compat with
   * call sites that haven't migrated yet. When both are passed, `stack` wins.
   */
  stack?: SecondaryDetail[];
  /** @deprecated Single-detail shorthand. Use `stack` instead. */
  detail?: SecondaryDetail | null;
  /**
   * Close handler for a specific stack index. Required when using `stack`.
   * Falls back to closing every panel when only the legacy `onClose` is
   * passed.
   */
  onCloseAt?: (index: number) => void;
  /**
   * Legacy single-panel close handler. When `stack` is in use, prefer
   * `onCloseAt`; this fires after every stack-clear (e.g. when the parent
   * primary panel closes).
   */
  onClose?: () => void;
  onOpenLinked: (type: RecordType, id: string) => void;
  isViewer?: boolean;
}

export function SecondaryDetailPanel({
  jobId,
  stack,
  detail,
  onCloseAt,
  onClose,
  onOpenLinked,
  isViewer,
}: SecondaryDetailPanelProps) {
  const flagCtx = useFlagDismissals(jobId);

  // ---------------------------------------------------------------------
  // Subscribe to every record list the secondary stack might reference.
  // These queries are small (one job's canonical entries) and Convex
  // de-dupes subscriptions across components, so loading them up-front is
  // cheaper than wiring each entry to its own per-type loader.
  // ---------------------------------------------------------------------

  const si = useQuery(
    convexQuery(
      api.jobs.summarization.publicQueries.listSecurityInterestEntriesForJob,
      { jobId, includesMerged: true },
    ),
  );
  const assets = useQuery(
    convexQuery(api.jobs.summarization.publicQueries.listAssetEntriesForJob, {
      jobId,
      includesMerged: true,
    }),
  );
  const liabilities = useQuery(
    convexQuery(
      api.jobs.summarization.publicQueries.listLiabilityEntriesForJob,
      { jobId, includesMerged: true },
    ),
  );
  const loans = useQuery(
    convexQuery(api.jobs.summarization.publicQueries.listLoanEntriesForJob, {
      jobId,
      includesMerged: true,
    }),
  );
  const atoSro = useQuery(
    convexQuery(api.jobs.summarization.publicQueries.listAtoSroEntriesForJob, {
      jobId,
    }),
  );
  // Director-disclosure label (ROCAP / SOA / …) for the loan panel headers.
  const { data: job } = useQuery(
    convexQuery(api.jobs.queries.getJob, { jobId }),
  );
  const disclosure = directorDisclosureLabel(job?.appointmentType);

  // Coerce the legacy single-detail prop into a stack so we always render
  // the same way. `stack` wins if both are passed.
  const items: SecondaryDetail[] =
    stack && stack.length > 0 ? stack : detail ? [detail] : [];

  if (items.length === 0) return null;

  const find = (rows: any, id: string): any =>
    ((rows ?? []) as any[]).find((r) => r._id === id) ?? null;

  const closeAt = (index: number) => {
    if (onCloseAt) {
      onCloseAt(index);
      return;
    }
    if (onClose) onClose();
  };

  return (
    <>
      {items.map((item, index) => {
        const stackIndex = index + 1;
        // Each stack item gets its own close handler so the user can peel
        // off panels one at a time without dismissing the rest.
        const onItemClose = () => closeAt(index);
        switch (item.type) {
          case "security_interest":
            return (
              <SecurityInterestDetailPanel
                key={`${item.type}:${item.id}`}
                entry={find(si.data, item.id)}
                open
                onClose={onItemClose}
                onDeleted={onItemClose}
                isViewer={isViewer}
                flagCtx={flagCtx}
                placement="secondary"
                stackIndex={stackIndex}
                onOpenLinked={onOpenLinked}
              />
            );
          case "asset":
            return (
              <AssetPanel
                key={`${item.type}:${item.id}`}
                entry={find(assets.data, item.id)}
                open
                onClose={onItemClose}
                isViewer={isViewer}
                flagCtx={flagCtx}
                placement="secondary"
                stackIndex={stackIndex}
                onOpenLinked={onOpenLinked}
              />
            );
          case "creditor":
            return (
              <LiabilityPanel
                key={`${item.type}:${item.id}`}
                entry={find(liabilities.data, item.id)}
                open
                onClose={onItemClose}
                onDeleted={onItemClose}
                isViewer={isViewer}
                placement="secondary"
                stackIndex={stackIndex}
                onOpenLinked={onOpenLinked}
              />
            );
          case "loan":
            return (
              <LoanDetailPanel
                key={`${item.type}:${item.id}`}
                entry={find(loans.data, item.id)}
                disclosure={disclosure}
                open
                onClose={onItemClose}
                onDeleted={onItemClose}
                isViewer={isViewer}
                placement="secondary"
                stackIndex={stackIndex}
                onOpenLinked={onOpenLinked}
              />
            );
          case "ato_sro":
            return (
              <AtoSroDetailSheet
                key={`${item.type}:${item.id}`}
                entry={find(atoSro.data, item.id)}
                open
                onClose={onItemClose}
                onDeleted={onItemClose}
                isViewer={isViewer}
                flagCtx={flagCtx}
                placement="secondary"
                stackIndex={stackIndex}
                onOpenLinked={onOpenLinked}
              />
            );
          default:
            return null;
        }
      })}
    </>
  );
}

/* eslint-enable @typescript-eslint/no-explicit-any */
